import { ReactNode } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

interface PageHeaderProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  /**
   * When true (default), shows a back arrow that goes to history.back() if possible,
   * otherwise falls back to `fallbackPath` ("/" by default).
   */
  showBack?: boolean;
  fallbackPath?: string;
  /** Right-aligned actions (buttons, toolbars). */
  actions?: ReactNode;
}

/**
 * Premium, consistent page header. Use at the top of pages linked from the
 * dashboard so users can always return. Typography matches the Analytics
 * redesign (eyebrow label, tight tracked title, muted subtitle).
 */
export function PageHeader({
  eyebrow,
  title,
  subtitle,
  showBack = true,
  fallbackPath = '/',
  actions,
}: PageHeaderProps) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(fallbackPath);
    }
  };

  return (
    <Box
      mb={3}
      display="flex"
      alignItems="flex-start"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={2}
    >
      <Box display="flex" alignItems="flex-start" gap={1.25}>
        {showBack && (
          <Tooltip title="Back" arrow>
            <IconButton
              onClick={handleBack}
              size="small"
              sx={{
                mt: eyebrow ? 2.25 : 0.75,
                width: 36,
                height: 36,
                border: '1px solid #E2E8F0',
                borderRadius: 1.5,
                color: '#475569',
                bgcolor: '#FFFFFF',
                transition: 'all 0.15s ease',
                '&:hover': {
                  color: '#0F172A',
                  borderColor: '#CBD5E1',
                  bgcolor: '#F8FAFC',
                },
                '&:active': { transform: 'translateY(1px)' },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        <Box>
          {eyebrow && (
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: '#94A3B8',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                mb: 0.5,
              }}
            >
              {eyebrow}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#0F172A',
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: '0.85rem', color: '#64748B', mt: 0.75 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {actions && (
        <Box display="flex" alignItems="center" gap={1}>
          {actions}
        </Box>
      )}
    </Box>
  );
}

export default PageHeader;
